import { useEffect, useState } from 'react';
import { api } from '../api';
import { Logo } from '../components';
import { Link, useRouter } from '../router';
import { useApp } from '../store';

export function ResetPassword() {
  const { refreshAuth, toast } = useApp();
  const { navigate } = useRouter();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const token = new URLSearchParams(window.location.search).get('token') ?? '';

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (password !== confirm) {
      setErrorMessage('Passwords do not match.');
      return;
    }
    setBusy(true);
    setErrorMessage('');
    try {
      await api.resetPassword(token, password);
      await refreshAuth();
      toast('Password updated — you are signed in.', 'success');
      navigate('/');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <main className="auth-page">
        <div className="auth-card">
          <Logo />
          <h1>Missing reset link</h1>
          <p>Open the link from your reset email, or request a new one.</p>
          <Link to="/login" className="secondary full">Back to sign in</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="auth-page">
      <div className="auth-card">
        <Logo />
        <h1>Choose a new password</h1>
        <p>This signs you out everywhere else, then signs you in here.</p>
        <form onSubmit={submit}>
          <label className="field">
            <span className="field-label">New password</span>
            <input value={password} onChange={(event) => setPassword(event.target.value)} type="password" minLength={8} autoComplete="new-password" required />
          </label>
          <label className="field">
            <span className="field-label">Confirm new password</span>
            <input value={confirm} onChange={(event) => setConfirm(event.target.value)} type="password" minLength={8} autoComplete="new-password" required />
          </label>
          {errorMessage && <div className="form-error" role="alert">{errorMessage}</div>}
          <button className="primary full" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Set new password'}</button>
        </form>
      </div>
    </main>
  );
}

export function VerifyEmail() {
  const token = new URLSearchParams(window.location.search).get('token') ?? '';
  const [state, setState] = useState<'working' | 'done' | 'failed'>(token ? 'working' : 'failed');

  useEffect(() => {
    if (!token) return;
    api.confirmEmailVerification(token)
      .then(() => setState('done'))
      .catch(() => setState('failed'));
  }, [token]);

  return (
    <main className="auth-page">
      <div className="auth-card">
        <Logo />
        {state === 'working' && <h1>Verifying…</h1>}
        {state === 'done' && (
          <>
            <h1>Email verified</h1>
            <p>You are set — reminders and account recovery will use this address.</p>
            <Link to="/" className="primary full">Back to TeamCraft</Link>
          </>
        )}
        {state === 'failed' && (
          <>
            <h1>Verification failed</h1>
            <p>This link is invalid or expired. Request a new one from your profile.</p>
            <Link to="/profile" className="secondary full">Go to profile</Link>
          </>
        )}
      </div>
    </main>
  );
}
